/**
 * @fileoverview AI Settings Hook
 * @description React hook for managing user AI preferences and BYOK
 * @module hooks/useAISettings
 * @version 1.0.0
 */

import { useState, useEffect, useCallback } from 'react';
import { aiSettingsService } from '@/services/core/ai-settings.service';
import { DEFAULT_AI_SETTINGS } from '@flowstate/types';
import type { AICredentialStatus, UserAISettings, AIModel, AIModelConfig, BoxAgentPreferences } from '@flowstate/types';
import { toast } from '@/components/ui/sonner';

interface UseAISettingsReturn {
  /** Current AI settings */
  settings: UserAISettings;
  /** Whether settings are loading */
  isLoading: boolean;
  /** Derived credential status (server-authoritative) */
  aiCredentialStatus: AICredentialStatus;
  /** Whether the user has any usable Cortex credential */
  isCortexReady: boolean;
  /** Whether user has a Gemini API key configured */
  hasGeminiKey: boolean;
  /** Whether user has an OpenRouter API key configured */
  hasOpenRouterKey: boolean;
  /** Update settings (saves to localStorage + syncs to Supabase) */
  updateSettings: (settings: Partial<UserAISettings>) => void;
  /** Set Gemini API key */
  setGeminiKey: (key: string) => void;
  /** Clear Gemini API key */
  clearGeminiKey: () => void;
  /** Set OpenRouter API key */
  setOpenRouterKey: (key: string) => void;
  /** Clear OpenRouter API key */
  clearOpenRouterKey: () => void;
  /** True when localStorage has a key not yet on server */
  needsByokServerSync: boolean;
  /** One-click migrate local BYOK to server */
  syncLocalByokToServer: () => void;
  /** Set preferred model */
  setModel: (model: AIModel) => void;
  /** Set task override */
  setOverride: (task: keyof Required<UserAISettings>['overrides'], model: AIModel) => void;
  /** Update model config */
  setModelConfig: (config: Partial<AIModelConfig>) => void;
  /** Update agent box preferences (CLI toggles, GitHub token) */
  updateBoxPrefs: (prefs: Partial<BoxAgentPreferences>) => void;
  /** Reset to defaults */
  resetSettings: () => void;
}

/**
 * Hook for managing AI settings
 */
export function useAISettings(): UseAISettingsReturn {
  const [settings, setSettings] = useState<UserAISettings>(DEFAULT_AI_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [aiCredentialStatus, setAiCredentialStatus] = useState<AICredentialStatus>(
    DEFAULT_AI_SETTINGS.aiCredentialStatus ?? 'none',
  );

  // Load from localStorage immediately for fast first paint
  useEffect(() => {
    const loaded = aiSettingsService.getSettings();
    setSettings(loaded.success ? loaded.data : DEFAULT_AI_SETTINGS);
    setAiCredentialStatus(
      (loaded.success ? loaded.data.aiCredentialStatus : undefined) ?? 'none',
    );
    setIsLoading(false);
  }, []);

  // Sync from Supabase (mycelium_principals.preferences.ai) once auth is ready.
  // This updates the state if the server has richer/newer data than localStorage.
  useEffect(() => {
    void aiSettingsService.syncFromPrincipal().then((result) => {
      if (result.success) {
        setSettings(result.data);
        setAiCredentialStatus(result.data.aiCredentialStatus ?? 'none');
      }
    });
  }, []);

  // Re-check server credential status whenever settings are mutated from the
  // wizard or other callers, so the gate updates immediately.
  useEffect(() => {
    void aiSettingsService.getCredentialStatus().then((status) => {
      setAiCredentialStatus(status);
    });
  }, [settings.geminiByokOnServer, settings.openrouterApiKeyOnServer, settings.provider]);

  const updateSettings = useCallback((updates: Partial<UserAISettings>) => {
    const updated = aiSettingsService.saveSettings(updates);
    if (updated.success) {
      setSettings(updated.data);
      void aiSettingsService.syncToPrincipal(updated.data);
    }
  }, []);

  const setGeminiKey = useCallback((key: string) => {
    const validation = aiSettingsService.validateGeminiKey(key);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }
    void aiSettingsService.setGeminiKey(key).then((updated) => {
      if (updated.success) {
        setSettings(updated.data);
        toast.success('Gemini API key saved');
      } else {
        toast.error(updated.error ?? 'Failed to save Gemini API key');
      }
    });
  }, []);

  const clearGeminiKey = useCallback(() => {
    void aiSettingsService.clearGeminiKey().then((updated) => {
      if (updated.success) {
        setSettings(updated.data);
        toast.success('Gemini API key removed');
      } else {
        toast.error(updated.error ?? 'Failed to remove Gemini API key');
      }
    });
  }, []);

  const setOpenRouterKey = useCallback((key: string) => {
    const validation = aiSettingsService.validateOpenRouterKey(key);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }
    void aiSettingsService.setOpenRouterKey(key).then((updated) => {
      if (updated.success) {
        setSettings(updated.data);
        toast.success('OpenRouter API key saved');
      } else {
        toast.error(updated.error ?? 'Failed to save OpenRouter API key');
      }
    });
  }, []);

  const clearOpenRouterKey = useCallback(() => {
    void aiSettingsService.clearOpenRouterKey().then((updated) => {
      if (updated.success) {
        setSettings(updated.data);
        toast.success('OpenRouter API key removed');
      } else {
        toast.error(updated.error ?? 'Failed to remove OpenRouter API key');
      }
    });
  }, []);

  const needsByokServerSync = !!settings.geminiApiKey && !settings.geminiByokOnServer;

  const syncLocalByokToServer = useCallback(() => {
    void aiSettingsService.syncLocalByokToServer().then((updated) => {
      if (updated.success) {
        setSettings(updated.data);
        toast.success('Gemini API key synced for quota fallback');
      } else {
        toast.error(updated.error ?? 'Failed to sync API key');
      }
    });
  }, []);

  const setModel = useCallback((model: AIModel) => {
    const updated = aiSettingsService.setPreferredModel(model);
    if (updated.success) {
      setSettings(updated.data);
    }
  }, []);

  const setOverride = useCallback((task: keyof Required<UserAISettings>['overrides'], model: AIModel) => {
    const updated = aiSettingsService.setOverride(task, model);
    if (updated.success) {
      setSettings(updated.data);
    }
  }, []);

  const setModelConfig = useCallback((config: Partial<AIModelConfig>) => {
    const updated = aiSettingsService.setModelConfig(config);
    if (updated.success) {
      setSettings(updated.data);
      void aiSettingsService.syncToPrincipal(updated.data);
    }
  }, []);

  const updateBoxPrefs = useCallback((prefs: Partial<BoxAgentPreferences>) => {
    const currentResult = aiSettingsService.getSettings();
    const current = currentResult.success ? currentResult.data : DEFAULT_AI_SETTINGS;
    const updated = aiSettingsService.saveSettings({
      box: { ...current.box, ...prefs },
    });
    if (updated.success) {
      setSettings(updated.data);
      // githubToken is stripped by syncToPrincipal before writing to Supabase
      void aiSettingsService.syncToPrincipal(updated.data);
    }
  }, []);

  const resetSettings = useCallback(() => {
    void aiSettingsService.resetToDefaults().then((reset) => {
      if (reset.success) {
        setSettings(reset.data);
        toast.success('AI settings reset to defaults');
      } else {
        toast.error(reset.error ?? 'Failed to reset AI settings');
      }
    });
  }, []);

  return {
    settings,
    isLoading,
    aiCredentialStatus,
    isCortexReady: aiCredentialStatus !== 'none',
    hasGeminiKey: !!settings.geminiApiKey || !!settings.geminiByokOnServer,
    hasOpenRouterKey: !!settings.openrouterApiKey || !!settings.openrouterApiKeyOnServer,
    needsByokServerSync,
    syncLocalByokToServer,
    updateSettings,
    setGeminiKey,
    clearGeminiKey,
    setOpenRouterKey,
    clearOpenRouterKey,
    setModel,
    setOverride,
    setModelConfig,
    updateBoxPrefs,
    resetSettings,
  };
}

export default useAISettings;
